import React, { useState, useEffect, useCallback } from 'react';
import { families as familiesApi, members as membersApi, memberList, familyList } from '../../api/endpoints';
import { getList, getTotal } from '../../api/client';
import { useFetchList, useNotification, usePrint } from '../../hooks';
import { getPreferences } from '../../utils/preferences';
import { avatarUrl } from '../../utils/avatar';
import { formatDate } from '../../utils/format';
import { syncHeadMemberOnEdit } from './familyHeadMember';
import { FAMILY_STATUS_LABEL } from '../members/lifeStatus';
import { getStoredUser, isAdmin } from '../../utils/auth';
import useStyles from '../../theme/useStyles';
import {
  PageHeader, SearchBar, SubTabs, Pagination, Notification, PromptDialog, FormLayout, Input, Select, Badge, Button, RowActions, TablePanel, PrintButton, Drawer
} from '../../components/ui';
import type { Family, Member } from '../../types';
import type { Column } from '../../components/ui';

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'TRANSFERRED', label: 'Transferred' },
  { value: 'INACTIVE', label: 'Inactive' }
];

/**
 * Status filter tabs. `all` is the landing tab, the middle three match
 * Family.status, and `VOID` reads the separate soft-delete flag.
 */
const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'ACTIVE', label: 'Active' },
  { key: 'TRANSFERRED', label: 'Transferred' },
  { key: 'INACTIVE', label: 'Inactive' },
  { key: 'CLOSED', label: 'Closed' },
  { key: 'VOID', label: '🗑️ Deleted', variant: 'danger' as const }
];

/** Rows with no stored status are treated as ACTIVE, as the table already does. */
const statusOf = (f) => f.status || 'ACTIVE';

/** Debounce a value — holds the search term steady between keystrokes. */
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/**
 * Soft-delete flag. Separate from `status` so deleting preserves whether the
 * family was Active, Transferred or Inactive. Stored as 'VOID' to match the
 * value every other module writes; the UI calls it "deleted".
 */
const isVoided = (f) => f.recordStatus === 'VOID';

const statusTone = (status: string) =>
  status === 'ACTIVE' ? 'success' : status === 'TRANSFERRED' ? 'warning' : 'neutral';

/** Editable fields plus `contact`, the column the backend actually reads. */
type FamilyForm = Partial<Family> & { contact?: string };

const EMPTY_FORM: FamilyForm = {
  id: null, familyId: '', familyName: '', membershipNo: '',
  headOfFamily: '', headGender: '', parish: '',
  contactNumber: '', email: '', address: '', status: 'ACTIVE',
  transferredDate: '', transferredTo: ''
};

const HEAD_GENDERS = ['Male', 'Female'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** 7 to 12 digits: Indian mobiles, landlines and numbers abroad. */
const MOBILE_RE = /^\d{7,12}$/;

/**
 * Field-level validation. Returns `{}` when the form is good, otherwise a map of
 * field -> message that the inputs render inline. `families` is passed in so
 * duplicates can be caught before the request is sent.
 */
const validateFamily = (form: FamilyForm, allFamilies: Family[], editingId): Record<string, string> => {
  const errors: Record<string, string> = {};
  const other = allFamilies.filter((f) => f.id !== editingId);
  const norm = (v) => String(v || '').trim().toLowerCase();

  const head = String(form.headOfFamily || '').trim();
  if (!head) errors.headOfFamily = 'Head of family is required.';
  else if (head.length < 3) errors.headOfFamily = 'Enter the full name (at least 3 characters).';

  const mobile = String(form.contactNumber || '').trim();
  if (!mobile) errors.contactNumber = 'Mobile number is required.';
  // Two families may share a contact number (relatives often do), so only the format is checked.
  else if (!MOBILE_RE.test(mobile)) errors.contactNumber = 'Enter a mobile number of 7 to 12 digits.';

  const email = String(form.email || '').trim();
  if (email && !EMAIL_RE.test(email)) errors.email = 'Enter a valid email address.';

  const address = String(form.address || '').trim();
  if (!address) errors.address = 'Address is required.';
  else if (address.length < 10) errors.address = 'Enter the full address (at least 10 characters).';

  const membershipNo = String(form.membershipNo || '').trim();
  if (membershipNo && other.some((f) => norm(f.membershipNo) === norm(membershipNo))) {
    errors.membershipNo = 'This membership number is already in use.';
  }

  // Two live families with the same head name is nearly always a double entry.
  if (head && other.some((f) => norm(f.headOfFamily) === norm(head))) {
    errors.headOfFamily = 'A family with this head of family already exists.';
  }

  if (!form.status) errors.status = 'Status is required.';

  // A transfer with no destination or date cannot be traced afterwards.
  if (form.status === 'TRANSFERRED') {
    if (!String(form.transferredTo || '').trim()) errors.transferredTo = 'Record which church the family moved to.';
    if (!String(form.transferredDate || '').trim()) errors.transferredDate = 'Record the date of transfer.';
  }

  return errors;
};

// Member lists don't always include the photo blob; fall back to fetching the
// single record before giving up on the generated avatar.
const SmartPhotoLoader = ({ member, fallbackSrc, size = '80px' }) => {
  const [imgSrc, setImgSrc] = useState(member?.photoDataUrl || fallbackSrc);

  useEffect(() => {
    if (!member) return undefined;
    let active = true;

    if (member.photoDataUrl) {
      setImgSrc(member.photoDataUrl);
      return undefined;
    }

    if (member.id) {
      membersApi
        .get(member.id)
        .then((data) => { if (active && data?.photoDataUrl) setImgSrc(data.photoDataUrl); })
        .catch(() => { /* generated avatar stays */ });
    }

    return () => { active = false; };
  }, [member, fallbackSrc]);

  return (
    <img
      src={imgSrc}
      alt="Profile"
      onError={(e) => {
        const img = e.currentTarget;
        img.onerror = null;
        img.src = fallbackSrc;
      }}
      style={{
        width: size, height: size, borderRadius: '50%', objectFit: 'cover',
        border: '2px solid #E0E0E0', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', backgroundColor: '#FFF'
      }}
    />
  );
};


const labelCell = { padding: '7px 10px', color: '#555', width: '150px', verticalAlign: 'top' as const };
const valueCell = { padding: '7px 10px', fontWeight: 'bold' as const, verticalAlign: 'top' as const };
// The fill and the rules of a table heading belong to the parish template —
// stated here as well, they painted over the template's coloured header band
// and left its white lettering on a pale grey strip, unreadable on paper.
const memberTh = {
  padding: '8px 10px', fontSize: '12px',
  textTransform: 'uppercase' as const, letterSpacing: '0.3px'
};
const memberTd = { padding: '8px 10px', border: '1px solid var(--pr-rule, #DDD)', verticalAlign: 'middle' as const };


const FamilyDetails = ({ isDarkMode, onAddMember, onEditFamily, headerSwitch, openFamily = null }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;


  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState<FamilyForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  /** A family that still has live members: they go with it, or the delete stops here. */
  const admin = isAdmin(getStoredUser());

  /**
   * The household being looked at, and the people in it. A family row carries
   * only a count, so the members are fetched when the drawer opens rather than
   * for every row in the table.
   */
  const [viewingFamily, setViewingFamily] = useState<Family | null>(null);
  const [viewMembers, setViewMembers] = useState<Member[]>([]);
  const [viewLoading, setViewLoading] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);

  const [statusTab, setStatusTab] = useState('all');
  const [errors, setErrors] = useState<Record<string, string>>({});
  /**
   * Drives the document view. `mode` decides what happens once it renders:
   */
  const [busyRowId, setBusyRowId] = useState(null);

  const { notification, notify, dismiss } = useNotification();
  const { print } = usePrint(notify);

  // The families list is server-paginated: the backend returns only the current
  // page for the active status tab and search, and each row already carries its
  // member and head counts. Nothing downloads every family or every member.
  const PAGE_SIZE = getPreferences().pageSize;
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm.trim(), 300);
  const [pageNum, setPageNum] = useState(1);
  const clear = useCallback(() => setSearchTerm(''), []);

  useEffect(() => { setPageNum(1); }, [statusTab, debouncedSearch]);

  const { data: families, raw: pageRaw, loading, refetch: refetchPage } =
    useFetchList<Family>((p, o) => familyList.page(p, o), {
      params: { bucket: statusTab, q: debouncedSearch, page: pageNum - 1, size: PAGE_SIZE }
    });
  const totalItems = getTotal(pageRaw);
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  // Tab counts from the backend.
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>(
    { all: 0, ACTIVE: 0, TRANSFERRED: 0, INACTIVE: 0, CLOSED: 0, VOID: 0 }
  );
  const refetchCounts = useCallback(async () => {
    try {
      const c = await familyList.counts();
      setStatusCounts({
        all: Number(c.all) || 0,
        ACTIVE: Number(c.ACTIVE) || 0,
        TRANSFERRED: Number(c.TRANSFERRED) || 0,
        INACTIVE: Number(c.INACTIVE) || 0,
        CLOSED: Number(c.CLOSED) || 0,
        VOID: Number(c.VOID) || 0
      });
    } catch { /* keep the last counts on a transient failure */ }
  }, []);
  useEffect(() => { refetchCounts(); }, [refetchCounts]);

  const refetch = useCallback(async () => {
    await Promise.all([refetchPage(), refetchCounts()]);
  }, [refetchPage, refetchCounts]);

  // The member count comes on the row from the backend (a correlated subquery),
  // so the client never loads the member table just to count.
  const memberCountOf = (f) => Number(f.memberCount) || 0;

  const familyKeyOf = (f: Family) => f.familyId || `FAM-${f.id}`;

  /** Live members of one household, head first, then the rest by name. */
  const loadMembers = async (f: Family): Promise<Member[]> => {
    const rows = getList<Member>(await memberList.byFamily(familyKeyOf(f)))
      .filter((m) => m.recordStatus !== 'VOID');
    const isHeadRow = (m: Member) => String(m.relationToHead || '').toLowerCase() === 'head';
    return rows.sort((a, b) => {
      if (isHeadRow(a) !== isHeadRow(b)) return isHeadRow(a) ? -1 : 1;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  };

  const openView = async (f: Family) => {
    setViewingFamily(f);
    setViewMembers([]);
    setViewLoading(true);
    try {
      setViewMembers(await loadMembers(f));
    } catch {
      notify('Could not load the members of this family.', 'error');
    } finally {
      setViewLoading(false);
    }
  };

  // Opened from the Approvals inbox ("Open the record"): the family, already loaded.
  useEffect(() => {
    if (openFamily) openView(openFamily);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openFamily]);

  /**
   * The household as one document: the family's own details, then every member
   * in it. Each member row carries the head's name, so a printed sheet says
   * whose house it is without the reader holding the family id in their head.
   */
  const printFamily = async (f: Family) => {
    let rows: Member[] = [];
    try {
      rows = await loadMembers(f);
    } catch {
      notify('Printing without the member list — it could not be loaded.', 'error');
    }
    print({
      module: 'Members & Families',
      documentType: 'FAMILY_RECORD',
      title: `Family-${familyKeyOf(f)}`,
      data: {
        ...f,
        familyId: familyKeyOf(f),
        memberCount: memberCountOf(f),
        rowCount: rows.length,
        rows: rows.map((m) => ({
          memberId: m.memberId || '',
          name: m.name || '',
          headOfFamily: f.headOfFamily || '',
          relationToHead: m.relationToHead || '',
          gender: m.gender || '',
          dateOfBirth: formatDate(m.dateOfBirth),
          mobileNumber: m.mobileNumber || '',
          memberStatus: m.memberStatus || ''
        }))
      }
    });
  };

  // Stands in for usePagination's shape so <Pagination> and the selection
  // helpers keep working — the server already applied the tab and search.
  const page = {
    currentItems: families,
    currentPage: pageNum,
    totalPages,
    totalItems,
    itemsPerPage: PAGE_SIZE,
    setCurrentPage: setPageNum,
    next: () => setPageNum((p) => Math.min(p + 1, totalPages)),
    prev: () => setPageNum((p) => Math.max(p - 1, 1)),
    isFirst: pageNum <= 1,
    isLast: pageNum >= totalPages,
    reset: () => setPageNum(1)
  };

  const switchStatusTab = (key) => {
    setStatusTab(key);
    setPageNum(1);
  };

  const closeForm = () => {
    setFormData(EMPTY_FORM);
    setEditingId(null);
    setErrors({});
    setShowForm(false);
  };

  const openFormForEdit = (family) => {
    setFormData({
      id: family.id,
      familyId: family.familyId || `FAM-${family.id}`,
      familyName: family.familyName || '',
      membershipNo: family.membershipNo || '',
      headOfFamily: family.headOfFamily || '',
      headGender: family.headGender || '',
      parish: family.parish || '',
      contactNumber: family.contactNumber || family.contact || '',
      email: family.email || '',
      address: family.address || '',
      status: family.status || 'ACTIVE',
      transferredDate: family.transferredDate || '',
      transferredTo: family.transferredTo || ''
    });
    setEditingId(family.id);
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Phone fields silently drop anything that isn't a digit, so a pasted
    // "98765 43210" becomes a clean number instead of an error.
    const clean = name === 'contactNumber' ? value.replace(/\D/g, '').slice(0, 12) : value;
    setFormData((prev) => ({ ...prev, [name]: clean }));
    // Clear this field's error as soon as the user edits it.
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const found = validateFamily(formData, families, editingId);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      notify('Please correct the highlighted fields.', 'error');
      return;
    }
    setErrors({});

    setSaving(true);
    try {
      const payload = { ...formData };
      // Trim every text field so " John " and "John" aren't two families.
      Object.keys(payload).forEach((k) => {
        if (typeof payload[k] === 'string') payload[k] = payload[k].trim();
      });
      if (!payload.familyName?.trim()) payload.familyName = `${payload.headOfFamily}'s Family`;
      payload.contact = payload.contactNumber; // backend reads `contact`

      // This form is edit-only — new families (and their head) are created from
      // the Members Add. Keep the saved row so the head member can be matched.
      const saved = await familiesApi.update(editingId, payload);
      await refetch();

      const savedFamily = saved || { ...payload, id: editingId };

      // If the head's name/gender/mobile changed here, keep the head member in
      // step. Only this family's members are fetched, not the whole table.
      // Best-effort — a family edit still succeeds if this write fails.
      try {
        const famKey = savedFamily.familyId || `FAM-${savedFamily.id}`;
        const famMembers = getList<Member>(await memberList.byFamily(famKey));
        await syncHeadMemberOnEdit(savedFamily, payload, famMembers);
      } catch { /* the family edit already saved */ }

      closeForm();
      notify('Family updated successfully.', 'success');
    } catch (err) {
      notify(err.message || 'Failed to save family details.', 'error');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Soft delete, matching every other module: the row moves to Voided with a
   * reason instead of being destroyed. `status` is left untouched so the record
   * still says whether the family was ACTIVE, TRANSFERRED or INACTIVE.
   */
  const deleteStamp = () => new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  const voidFamilyRow = (family, reason) => familiesApi.update(family.id, {
    ...family,
    contact: family.contactNumber || family.contact,
    recordStatus: 'VOID',
    deleteReason: reason,
    deletedAt: deleteStamp()
  });

  /**
   * Members point at their family by id, so deleting a family that still has
   * live members would orphan them. The delete therefore looks first: an empty
   * family goes straight to the reason prompt; one with members opens the
   * dialog that takes them along or stops.
   */
  const requestDelete = async (family) => {
    const familyIdStr = family.familyId || `FAM-${family.id}`;
    const live = getList<Member>(await memberList.byFamily(familyIdStr).catch(() => []))
      .filter((m) => m.recordStatus !== 'VOID');
    if (live.length > 0) {
      notify(`${familyIdStr} has ${live.length} member${live.length === 1 ? '' : 's'}. A family that left gets a new status `
        + '(Edit › Status: Inactive or Transferred). Remove it only if it was typed by mistake — remove its members first.', 'error');
      return;
    }
    setPendingDelete(family);
  };

  const handleVoid = async (reason) => {
    const family = pendingDelete;
    setPendingDelete(null);
    // Guard: the dialog can only reach here with a row selected, but the
    // state is nulled before the await so TypeScript cannot prove it.
    if (!family) return;
    try {
      await voidFamilyRow(family, reason);
      await refetch();
      setSelectedIds((prev) => prev.filter((id) => id !== family.id));
      notify('Wrong entry removed. You can find it under Deleted History.', 'success');
    } catch (err) {
      notify(err.message || 'Failed to delete the family.', 'error');
    }
  };

  /** Puts a voided family back into the live list. */
  const handleRestore = async (family) => {
    try {
      await familiesApi.update(family.id, {
        ...family,
        contact: family.contactNumber || family.contact,
        recordStatus: 'ACTIVE',
        deleteReason: null,
        deletedAt: null
      });
      await refetch();
      notify('Family restored.', 'success');
    } catch (err) {
      notify(err.message || 'Failed to restore the family.', 'error');
    }
  };

  // --- Selection ---
  const currentIds = page.currentItems.map((f) => f.id);
  const isAllCurrentSelected = currentIds.length > 0 && currentIds.every((id) => selectedIds.includes(id));

  const toggleSelectAll = (e) => {
    if (e.target.checked) setSelectedIds((prev) => [...new Set([...prev, ...currentIds])]);
    else setSelectedIds((prev) => prev.filter((id) => !currentIds.includes(id)));
  };

  const toggleSelectOne = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // The list row is a summary; the detail call fills in the rest and this
  // family's members are fetched on demand (not from a whole-table load). The
  const loadFamilyWithMembers = async (id) => {
    const base = families.find((f) => f.id === id) || {};
    const familyIdStr = base.familyId || `FAM-${id}`;
    const [detail, membersList] = await Promise.all([
      familiesApi.get(id).catch(() => null),
      memberList.byFamily(familyIdStr).then(getList).catch(() => [] as Member[])
    ]);
    return { ...base, ...(detail || {}), members: membersList };
  };

  /** Filename the browser suggests in the Save-as-PDF dialog. */
  const pdfNameFor = (family) =>
    `Family-${family.familyId || `FAM-${family.id}`}-${String(family.headOfFamily || 'record').replace(/[^\w-]+/g, '-')}`;


  /**


  /** Bulk: every ticked family in one document. */

  /**
   * The hidden document has to be in the DOM before the dialog opens, so the
   * call is made from an effect once it has rendered. Browsers take the default
   * Save-as-PDF filename from document.title, so it is swapped for the duration.
   */

  const columns: Array<Column<Family>> = [
    {
      key: 'select',
      width: '40px',
      align: 'center',
      header: <input type="checkbox" checked={isAllCurrentSelected} onChange={toggleSelectAll} style={{ cursor: 'pointer' }} />,
      // stopPropagation: the row itself opens the record, and ticking a box
      render: (f) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(f.id)}
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggleSelectOne(f.id)}
          style={{ cursor: 'pointer' }}
        />
      )
    },
    // Alignment follows one rule: identifiers and text read left, counts and
    // numbers read right so they can be compared down the column, and badges
    // and controls sit centred or right against the edge they belong to.
    {
      key: 'familyId',
      header: 'Family ID',
      width: '110px',
      nowrap: true,
      render: (f) => <strong>{f.familyId || `FAM-${f.id}`}</strong>
    },
    {
      key: 'headOfFamily',
      header: 'Head of Family',
      render: (f) => (
        <>
          <strong>{f.headOfFamily}</strong>
          <div style={{ fontSize: t.fontSize.sm, color: t.textFaint }}>
            {f.familyName || `${f.headOfFamily}'s Family`}
          </div>
        </>
      )
    },
    // Two facts per cell keeps the row short while the table stays informative,
    // instead of one thin column and a wide empty stretch.
    {
      key: 'contactNumber',
      header: 'Contact',
      width: '190px',
      render: (f) => (
        <>
          <div style={{ fontVariantNumeric: 'tabular-nums' }}>{f.contactNumber || f.contact || '—'}</div>
          <div style={{ fontSize: t.fontSize.sm, color: t.textFaint, wordBreak: 'break-all' }}>
            {f.email || '—'}
          </div>
        </>
      )
    },
    {
      key: 'parish',
      header: 'Parish / Membership',
      width: '180px',
      render: (f) => (
        <>
          <div>{f.parish || '—'}</div>
          <div style={{ fontSize: t.fontSize.sm, color: t.textFaint }}>
            {f.membershipNo ? `No. ${f.membershipNo}` : 'No membership no.'}
          </div>
        </>
      )
    },
    {
      key: 'address',
      header: 'Address',
      render: (f) => (
        <span style={{ fontSize: t.fontSize.sm }}>{f.address || '—'}</span>
      )
    },
    {
      // The count already includes the head, so beside a Head column naming
      // one person it read as that many people *besides* the head (CHM-140).
      // Renamed, and the head's share of the number is spelled out.
      key: 'members',
      header: 'Family Size',
      width: '120px',
      align: 'right',
      nowrap: true,
      render: (f) => {
        const n = memberCountOf(f);
        if (n <= 0) return <span style={{ color: t.textFaint }}>0</span>;
        return (
          <>
            <strong>{n}</strong>
            <div style={{ fontSize: t.fontSize.sm, color: t.textFaint }}>
              {n === 1 ? 'head only' : `head + ${n - 1}`}
            </div>
          </>
        );
      }
    },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      align: 'center',
      render: (f) => (isVoided(f)
        ? <Badge tone="error">DELETED</Badge>
        : <Badge tone={statusTone(statusOf(f))}>{FAMILY_STATUS_LABEL[statusOf(f)] || statusOf(f)}</Badge>)
    },
    { key: 'createdBy', header: 'Created By', render: (f) => (f as { createdBy?: string }).createdBy || '-' },
    {
      key: 'actions',
      header: 'Action',
      width: '56px',
      align: 'right',
      nowrap: true,
      render: (f) => {
        const busy = busyRowId === f.id;
        return (
          <span onClick={(e) => e.stopPropagation()}>
          <RowActions
            isDarkMode={isDarkMode}
            items={isVoided(f)
              ? [{ icon: '♻️', label: 'Restore', onClick: () => handleRestore(f) }]
              : [
                  { icon: '👁️', label: 'View', onClick: () => openView(f) },
                  // Editing a family means editing its head — head and family
                  // together on the Members page, the same form they are created
                  // on. Falls back to the local form only outside the merged screen.
                  {
                    icon: '✏️', label: 'Edit', write: true,
                    onClick: () => (onEditFamily
                      ? onEditFamily(f.familyId || `FAM-${f.id}`)
                      : openFormForEdit(f))
                  },
                  // Only when hosted in the merged screen: jump to Members ▸ +Add
                  // with this family pre-selected.
                  onAddMember && {
                    icon: '➕', label: 'Add member', write: true, separated: true,
                    onClick: () => onAddMember(f.familyId || `FAM-${f.id}`)
                  },
                  {
                    icon: '🖨️', label: 'Print / Save PDF', separated: true,
                    onClick: () => printFamily(f)
                  },
                  admin && { icon: '🗑️', label: 'Remove wrong entry', separated: true, danger: true, onClick: () => requestDelete(f) }
                ]}
          />
          </span>
        );
      }
    }
  ];

  // Voided rows explain themselves instead of offering actions that don't apply.
  // Anchored to the Status column by name so adding columns cannot misplace it.
  if (statusTab === 'VOID') {
    columns.splice(columns.findIndex((c) => c.key === 'status') + 1, 0,
      { key: 'deletedAt', header: 'Deleted On', width: '120px', nowrap: true,
        render: (f) => <span style={{ fontSize: t.fontSize.sm, color: t.textFaint }}>{f.deletedAt || '—'}</span> },
      { key: 'deleteReason', header: 'Reason',
        render: (f) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{f.deleteReason || '—'}</span> }
    );
  }

  return (
    <div style={styles.container}>
      <Notification notification={notification} onClose={dismiss} />

      <PageHeader
        // The Families/Members switch is the header inside the merged screen;
        // standalone it keeps its own title. The header keeps only Deleted
        // History; the status filters sit in the search row, beside the field.
        leading={headerSwitch}
        icon={headerSwitch ? undefined : '👨‍👩‍👧‍👦'}
        title={headerSwitch ? undefined : 'Family Details'}
        tabs={showForm ? [] : STATUS_TABS.filter((tab) => tab.key === 'VOID').map((tab) => ({
          ...tab,
          label: `${tab.label} (${statusCounts[tab.key] ?? 0})`
        }))}
        activeTab={statusTab}
        onTabChange={switchStatusTab}
      />

      {showForm ? (
        <FormLayout
          title="Edit Family"
          submitLabel="Save Family"
          submitting={saving}
          onSubmit={handleSubmit}
          onCancel={closeForm}
        >
          <FormLayout.Grid>
            <Input
              label="Family Name" name="familyName"
              placeholder="Defaults to the head of family's name"
              value={formData.familyName} onChange={handleChange}
            />
            <Input
              label="Membership No." name="membershipNo"
              placeholder="Parish membership number"
              value={formData.membershipNo} onChange={handleChange}
              error={errors.membershipNo}
            />
            <Input
              label="Head of Family" name="headOfFamily" required
              placeholder="Enter full name"
              value={formData.headOfFamily} onChange={handleChange}
              error={errors.headOfFamily}
            />
            <Select
              label="Head's Gender" name="headGender"
              options={HEAD_GENDERS}
              value={formData.headGender} onChange={handleChange}
            />
            <Input
              label="Mob No." name="contactNumber" required
              placeholder="e.g. 9876543210" maxLength={12} inputMode="numeric"
              value={formData.contactNumber} onChange={handleChange}
              error={errors.contactNumber}
              hint={!errors.contactNumber && '7 to 12 digits'}
            />
            <Input
              label="Email" name="email" type="email"
              placeholder="e.g. family@example.com"
              value={formData.email} onChange={handleChange}
              error={errors.email}
            />
            <Input
              label="Parish" name="parish"
              placeholder="e.g. St. Thomas Parish"
              value={formData.parish} onChange={handleChange}
            />
            <Select
              label="Status" name="status" placeholder=""
              options={formData.status === 'CLOSED' ? [...STATUS_OPTIONS, { value: 'CLOSED', label: 'Closed' }] : STATUS_OPTIONS}
              value={formData.status} onChange={handleChange}
              error={errors.status}
              hint="The members follow: Transferred or Inactive applies to every active member; Active brings back those who were inactive or moved with the family. Closed is set automatically when no living member is left."
            />
            {/* Only meaningful once the family has actually moved. */}
            {formData.status === 'TRANSFERRED' && (
              <>
                <Input
                  label="Transferred To" name="transferredTo" required
                  placeholder="Name of the receiving church"
                  value={formData.transferredTo} onChange={handleChange}
                  error={errors.transferredTo}
                />
                <Input
                  label="Date of Transfer" name="transferredDate" type="date" required
                  value={formData.transferredDate} onChange={handleChange}
                  error={errors.transferredDate}
                />
              </>
            )}
            <Input
              label="Address" name="address" type="textarea" required fullWidth
              placeholder="Full residential address..."
              value={formData.address} onChange={handleChange}
              error={errors.address}
            />
          </FormLayout.Grid>
        </FormLayout>
      ) : (
        <>
          {/* One row: a compact search field with the status tabs beside it.
              Wraps on a narrow screen. */}
          <SearchBar
            value={searchTerm}
            onChange={(v) => { setSearchTerm(v); page.reset(); }}
            onClear={() => { clear(); page.reset(); }}
            placeholder="Search by Head Name or Mob No..."
            fieldStyle={{ flex: '0 1 300px', minWidth: '200px' }}
            style={{ flexWrap: 'wrap', alignItems: 'center' }}
            actions={
              <>
                <PrintButton
                  module="Members & Families"
                  documentType="FAMILY_REGISTER"
                  size="sm"
                  title="Family-Register"
                  data={() => ({
                    title: 'Family Register',
                    reference: `${statusTab} families`,
                    rowCount: page.currentItems.length,
                    // The rows are the module's own records, untouched, so a
                    // template names the fields this register actually stores.
                    rows: page.currentItems
                  })}
                  onNotify={notify}
                  isDarkMode={isDarkMode}
                />
              <SubTabs
                value={statusTab}
                onChange={switchStatusTab}
                style={{ marginBottom: 0 }}
                tabs={STATUS_TABS.filter((tab) => tab.key !== 'VOID').map((tab) => ({
                  ...tab,
                  label: `${tab.label} (${statusCounts[tab.key] ?? 0})`
                }))}
              />
              </>
            }
          />

          <TablePanel
            title="Families"
            below={<Pagination {...page} itemsOnPage={page.currentItems.length} label="families" />}
            columns={columns}
            rows={page.currentItems}
            loading={loading}
            emptyMessage="No families found."
            rowStyle={(f) => (selectedIds.includes(f.id)
              ? { backgroundColor: isDarkMode ? '#2A3B4C' : '#E3F2FD' }
              : null)}
          />
        </>
      )}


      {/* The household at a glance: its own details, then everyone in it. */}
      {viewingFamily && (
        <Drawer
          open
          title={viewingFamily.headOfFamily || familyKeyOf(viewingFamily)}
          subtitle={`${familyKeyOf(viewingFamily)} · Head of Family`}
          onClose={() => setViewingFamily(null)}
          isDarkMode={isDarkMode}
          footer={
            <Button fullWidth onClick={() => printFamily(viewingFamily)}>
              🖨️ Print / Save PDF
            </Button>
          }
        >
          <Drawer.Section title="Family" />
          <Drawer.Row label="Family ID" value={familyKeyOf(viewingFamily)} />
          <Drawer.Row label="Head of Family" value={viewingFamily.headOfFamily} />
          <Drawer.Row label="Family Name" value={viewingFamily.familyName} />
          <Drawer.Row label="Membership No" value={viewingFamily.membershipNo} />
          <Drawer.Row label="Contact" value={viewingFamily.contactNumber || viewingFamily.contact} />
          <Drawer.Row label="Email" value={viewingFamily.email} />
          <Drawer.Row label="Parish" value={viewingFamily.parish} />
          <Drawer.Row label="Address" value={viewingFamily.address} />
          <Drawer.Row label="Status" value={viewingFamily.status} />

          <Drawer.Section title={`Members (${viewMembers.length})`} />
          {viewLoading && <p style={{ color: t.textMuted, margin: '6px 0' }}>Loading members…</p>}
          {!viewLoading && viewMembers.length === 0 && (
            <p style={{ color: t.textMuted, margin: '6px 0' }}>No members are linked to this family yet.</p>
          )}
          {viewMembers.map((m) => (
            <div
              key={m.id}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: '10px', flexWrap: 'wrap', padding: '9px 0',
                borderBottom: `1px solid ${t.border}`
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 'bold', color: t.textMain }}>
                  {m.name}
                  {String(m.relationToHead || '').toLowerCase() === 'head' && (
                    <Badge tone="success" style={{ marginLeft: '8px' }}>Head</Badge>
                  )}
                </div>
                {/* The head is named against every member, so the sheet reads
                    without holding the family id in mind. */}
                <div style={{ fontSize: t.fontSize.sm, color: t.textFaint }}>
                  {m.memberId} · {m.relationToHead || '-'} of {viewingFamily.headOfFamily || '-'}
                </div>
              </div>
              <div style={{ textAlign: 'right', fontSize: t.fontSize.sm, color: t.textMuted }}>
                <div>{m.gender || '-'}{m.dateOfBirth ? ` · ${formatDate(m.dateOfBirth)}` : ''}</div>
                <div>{m.mobileNumber || '-'}</div>
              </div>
            </div>
          ))}
        </Drawer>
      )}

      <PromptDialog
        open={Boolean(pendingDelete)}
        title={`Remove wrong entry — ${pendingDelete?.familyId || 'Family'}`}
        message={`Remove the family record for ${pendingDelete?.headOfFamily || 'this family'} as a wrong entry (for example typed twice)? It moves to Deleted History and can be brought back. Enter the reason:`}
        placeholder="e.g. Duplicate of FAM-8003"
        confirmLabel="Remove"
        requiredError="A reason is required."
        onSubmit={handleVoid}
        onCancel={() => setPendingDelete(null)}
      />

    </div>
  );
};

export default FamilyDetails;
